import type { FC, ReactNode } from "react";
import { Platform, Pressable, View, type StyleProp, type ViewStyle } from "react-native";
import { StyleSheet } from "react-native-unistyles";
import "../../theme";

export interface SnapshotCardProps {
  /** Content rendered in the snapshot (image) area. Fills the container with overflow hidden. */
  readonly renderSnapshot: () => ReactNode;
  /** Content rendered below the snapshot. Receives a constrained container with overflow hidden. */
  readonly renderDetail?: (() => ReactNode) | undefined;
  /**
   * Fixed height for the detail slot (in pixels). When set, the detail area is
   * constrained to this height — essential for virtualised lists that need a
   * predictable total card height. Visual clipping of overflow is handled by
   * the outer card's `overflow: 'hidden'`. When `undefined`, the detail area
   * sizes to its content.
   */
  readonly detailHeight?: number | undefined;
  /**
   * Aspect ratio for the snapshot area expressed as width/height (e.g. 16/9).
   * Defaults to 4/3. Ignored when `snapshotHeight` is set.
   */
  readonly snapshotAspectRatio?: number | undefined;
  /**
   * Fixed pixel height for the snapshot area. When set, takes precedence over
   * `snapshotAspectRatio` — use this in virtualised lists where a predictable
   * total card height is required (aspect ratio depends on card width, which
   * `getItemLayout` can't know ahead of time).
   */
  readonly snapshotHeight?: number | undefined;
  /** Border radius applied to the card corners. Defaults to theme spacing.md (8). */
  readonly borderRadius?: number | undefined;
  /**
   * When provided, the entire card becomes pressable with opacity feedback.
   * Mutually exclusive with wrapping in an external pressable (e.g. NavPressable,
   * Link) — do not combine both, as nested pressables cause gesture conflicts on
   * native and invalid interactive nesting on web. If you need router navigation,
   * omit `onPress` and wrap the card externally instead.
   */
  readonly onPress?: (() => void) | undefined;
  /** Accessibility label for the pressable card. */
  readonly accessibilityLabel?: string | undefined;
  /** Whether the card is in a disabled (non-interactive) state. */
  readonly disabled?: boolean | undefined;
  /** Optional style merged onto the outer container. */
  readonly style?: StyleProp<ViewStyle> | undefined;
}

const SnapshotCard: FC<SnapshotCardProps> = ({
  renderSnapshot,
  renderDetail,
  detailHeight,
  snapshotAspectRatio = 4 / 3,
  snapshotHeight,
  borderRadius,
  onPress,
  accessibilityLabel,
  disabled = false,
  style,
}) => {
  const resolvedRadius = borderRadius ?? styles.card.borderRadius;
  const cardStyle: StyleProp<ViewStyle> = [styles.card, { borderRadius: resolvedRadius }, style];

  const snapshotSizing: ViewStyle =
    snapshotHeight != null ? { height: snapshotHeight } : { aspectRatio: snapshotAspectRatio };

  const content = (
    <>
      <View style={[styles.snapshotContainer, snapshotSizing]}>{renderSnapshot()}</View>

      {renderDetail && <View style={[detailHeight != null && { height: detailHeight }]}>{renderDetail()}</View>}
    </>
  );

  if (onPress && !disabled) {
    return (
      <Pressable
        onPress={onPress}
        accessibilityRole="button"
        accessibilityLabel={accessibilityLabel}
        style={({ pressed }) => [cardStyle, pressed && styles.pressed, Platform.OS === "web" && styles.webInteractive]}
      >
        {content}
      </Pressable>
    );
  }

  return <View style={cardStyle}>{content}</View>;
};

SnapshotCard.displayName = "SnapshotCard";

export { SnapshotCard };

const styles = StyleSheet.create((theme) => ({
  card: {
    backgroundColor: theme.colors.surface,
    borderRadius: theme.spacing.md,
    overflow: "hidden",
  },
  snapshotContainer: {
    width: "100%",
    overflow: "hidden",
    backgroundColor: theme.colors.borderCard,
  },
  pressed: {
    opacity: 0.85,
  },
  webInteractive: Object.assign({ cursor: "pointer" } satisfies ViewStyle, {
    transition: "opacity 0.15s ease-in-out",
  }),
}));
